import React from "react";
import { View, Pressable, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CheckedBadge from "../../svgs/common/CheckedBadge";
import CustomProgressBar from "../../common/general/CustomProgressBar/CustomProgressBar";
import { CalendarDots } from "@phosphor-icons/react/dist/csr/CalendarDots";
import { SealCheck } from "@phosphor-icons/react/dist/csr/SealCheck";

function AiTutorCardProgress({
  icon,
  title,
  isHovered,
  isComplete = false,
  onPress,
  onHoverIn,
  onHoverOut,
  totalObjectives,
  completedObjectives,
  dueDate,
}) {
  const progressPercentage = totalObjectives > 0 ? (completedObjectives / totalObjectives) * 100 : 0;

  // Helper function to format due date
  const formatDueDate = (dateString) => {
    if (!dateString) return null;

    const dueDate = new Date(dateString);
    const today = new Date();

    const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    return {
      text: `Due ${month}/${day}`,
      isOverdue: dueDateStart < todayStart,
    };
  };

  const dueDateInfo = formatDueDate(dueDate);

  return (
    <>
      <Pressable
        onPress={onPress}
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        style={({ pressed }) => [
          styles.lessonItem,
          isHovered && styles.lessonItemHovered,
          pressed && styles.lessonItemPressed,
        ]}>
        <View style={styles.lessonItemLeft}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {icon && <View style={{ marginRight: 10 }}>{icon}</View>}
            <CustomText text={title} weight="bold" style={styles.lessonItemTitle} useTranslationText={false} />
          </View>

          <View style={styles.progressContainer}>
            <View style={styles.progressStatus}>
              <View style={styles.progressBar}>
                <CustomProgressBar
                  ariaLabel="Course progress bar"
                  progress={progressPercentage}
                  color={isComplete ? "#22C55E" : "#3E68FE"}
                />
              </View>
              <CustomText
                text={`${Math.round(progressPercentage)}% Complete`}
                useTranslationText={false}
                textType="text"
                weight="normal"
                size="xs"
              />
              {dueDateInfo && (
                <View style={[styles.dueDatePill, dueDateInfo.isOverdue && styles.overduePill]}>
                  <CalendarDots
                    size={14}
                    color={dueDateInfo.isOverdue ? "#DC2626" : "#344054"}
                    style={styles.calendarIcon}
                  />
                  <CustomText
                    weight="bold"
                    text={dueDateInfo.text}
                    size="s"
                    style={[styles.dueDateText, dueDateInfo.isOverdue && styles.overdueText]}
                    useTranslationText={false}
                  />
                </View>
              )}
            </View>
          </View>
        </View>
        {isComplete ? <CheckedBadge /> : <SealCheck size={24} color="#525457" />}
      </Pressable>
    </>
  );
}

const styles = StyleSheet.create({
  progressBar: {
    width: 150,
    maxWidth: 150,
  },
  lessonItem: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  lessonItemHovered: {
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  lessonItemPressed: {
    backgroundColor: "#F5F5F5",
  },
  lessonItemLeft: {
    flex: 1,
    marginRight: 16,
  },
  lessonItemTitle: {
    marginBottom: 4,
  },
  progressContainer: {
    marginTop: 4,
    gap: 8,
  },
  progressStatus: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 12,
  },
  dueDatePill: {
    backgroundColor: "#EAECF0",
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 360,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  overduePill: {
    backgroundColor: "#FEE2E2",
  },
  calendarIcon: {
    marginRight: 2,
  },
  dueDateText: {
    color: "#667085",
    fontSize: 12,
    fontWeight: "500",
  },
  overdueText: {
    color: "#DC2626",
  },
});

export default AiTutorCardProgress;
